import Controller from "./application_controller"

/**
 * Defers connecting child Stimulus controllers until a delegated event happens.
 *
 * Use this on a stable ancestor when rendering many repeated elements where each
 * child controller is only needed after interaction.
 *
 *   <div
 *     data-controller="deferred"
 *     data-deferred-event-value="focusin mousedown"
 *     data-deferred-selector-value="select[data-deferred-controller]">
 *
 *     <select data-deferred-controller="lazy-select"></select>
 *   </div>
 *
 * When a matching event bubbles from a matching element, this controller moves
 * the identifiers from data-deferred-controller into data-controller:
 *
 *   data-deferred-controller="lazy-select"
 *   becomes
 *   data-controller="lazy-select"
 *
 * If the target controller exposes a global class at
 * window.MinuteDock.<ControllerName>Controller with a deferredControllerAttached
 * static method, that hook is called immediately after attachment. For example,
 * lazy-select uses this to populate options during the same mousedown/focusin
 * interaction that attached the controller.
 */
export default class extends Controller {
  static values = {
    event: { type: String, default: "focusin" },
    selector: { type: String, default: "[data-deferred-controller]" }
  }

  connect() {
    this.eventNames.forEach((eventName) => {
      this.addEventListener(eventName, this.attach.bind(this))
    })
  }

  attach(event) {
    const target = event.target.closest?.(this.selectorValue)
    if (!target || !this.element.contains(target)) return

    const controllerIdentifiers = this.deferredControllerIdentifiersFor(target)
    if (controllerIdentifiers.length == 0) return

    this.addControllers(target, controllerIdentifiers)
    delete target.dataset.deferredController

    controllerIdentifiers.forEach((identifier) => {
      this.notifyAttached(target, identifier)
    })
  }

  addControllers(element, controllerIdentifiers) {
    const existingIdentifiers = this.controllerIdentifiersFor(element.dataset.controller)
    const identifiers = [...new Set(existingIdentifiers.concat(controllerIdentifiers))]

    element.dataset.controller = identifiers.join(" ")
  }

  notifyAttached(element, identifier) {
    const controllerClass = window.MinuteDock?.[this.controllerClassName(identifier)]
    controllerClass?.deferredControllerAttached?.(element)
  }

  controllerIdentifiersFor(identifiers = "") {
    return identifiers.split(/\s+/).filter(Boolean)
  }

  deferredControllerIdentifiersFor(element) {
    return this.controllerIdentifiersFor(element.dataset.deferredController || "")
  }

  controllerClassName(identifier) {
    const name = identifier.split("-").map((part) => {
      return part.charAt(0).toUpperCase() + part.slice(1)
    }).join("")

    return `${name}Controller`
  }

  get eventNames() {
    return this.eventValue.split(/\s+/).filter(Boolean)
  }
}
